import { ethers } from "ethers";
import {
  SHIELDED_POOL_JOINSPLIT_V2_INTERFACE,
  buildFlatJoinSplitV2Transaction,
  buildFlatMigrateV2Transaction,
  buildFlatPrivateBridgeV2Transaction,
  buildFlatPrivateSwapV2Transaction,
  buildFlatWithdrawBatchV2Transaction,
  buildFlatWithdrawV2Transaction,
  validateFlatJoinSplitV2WithdrawBatchEntries,
  computeFlatJoinSplitV2BridgeAuxHash,
  computeFlatJoinSplitV2SwapAuxHash,
  proveFlatJoinSplitV2,
  readFlatJoinSplitV2PoolSnapshot,
  resolveFlatJoinSplitV2Action,
  selectFlatJoinSplitV2Arity,
  simulateFlatJoinSplitV2Transaction,
  type FlatJoinSplitV2BridgeData,
  type FlatJoinSplitV2Hex32,
  type FlatJoinSplitV2HexData,
  type FlatJoinSplitV2InputNoteWitness,
  type FlatJoinSplitV2OutputNoteWitness,
  type FlatJoinSplitV2ProveRequest,
  type FlatJoinSplitV2SwapData,
} from "./sdk";
import { EVM_CONFIG } from "../config";
import {
  submitBasePoolRelay,
  serializeTransaction,
} from "../utils/relayer-client";
import { computeNPK, computeNullifier } from "../joinsplit/keys";
import type {
  JoinSplitNote,
  SpendingKeys,
  StoredJoinSplitNote,
} from "../joinsplit/types";
import {
  scanFlatJoinSplitV2Tree,
  type FlatJoinSplitV2MerklePath,
} from "./merkleIndex";
import {
  assertFlatJoinSplitV2Note,
  createFlatJoinSplitV2Note,
  flatFieldHex,
} from "./notes";
import { withFlatJoinSplitV2Prover } from "./prover";
import { assertAscendingLeafIndices, selectForAmount } from "./spendSelection";
import {
  markFlatJoinSplitV2SettlementBroadcast,
  markFlatJoinSplitV2SettlementFinalized,
  markFlatJoinSplitV2SettlementIncluded,
  prepareFlatJoinSplitV2SettlementJournal,
  type FlatJoinSplitV2JournalDelivery,
  type FlatJoinSplitV2JournalKind,
  type FlatJoinSplitV2JournalScope,
} from "./pendingJournal";
import { fail } from "./common";
import type {
  FlatJoinSplitV2BatchWithdrawParams,
  FlatJoinSplitV2OperationResult,
  FlatJoinSplitV2WithdrawParams,
} from "./operationTypes";
import {
  authenticateAndDedupeCandidates,
  candidateNotes,
  selectExact,
} from "./noteSelection";
import {
  readCurrentTokenDomain,
  requireEnabled,
  requireProvider,
} from "./poolAccess";
import { proveAndSend } from "./proveAndSend";
import { selfDeliveries } from "./witness";
import { usesPrivateFees, executePrivateFeeSpend } from "./privateFees";
import { approvedWithdrawalKind } from "./privateFeeApproval";
import { assertPublicWithdrawalRecipient } from "./withdrawalRecipient";
import {
  approvedRelayFeeCovers,
  usesPoolRelayQuotes,
  type PoolRelayQuoteShape,
} from "./poolRelayQuote";

/** On a quoted network, re-price the exact settlement the notes were just
 * selected for, before anything is proven: the relayer must still accept the
 * fee the user approved. It is never raised silently. */
async function assertQuotedRelayFee(
  shape: PoolRelayQuoteShape,
  approvedFee: bigint,
  signal?: AbortSignal,
): Promise<void> {
  if (!(await approvedRelayFeeCovers(shape, approvedFee, { signal })))
    fail(
      "network fees rose since this fee was quoted; review the withdrawal again",
    );
}

export async function executeFlatJoinSplitV2Withdraw(
  params: FlatJoinSplitV2WithdrawParams,
): Promise<FlatJoinSplitV2OperationResult> {
  try {
    assertPublicWithdrawalRecipient(params.recipient);
    if (usesPrivateFees())
      return executePrivateFeeSpend(
        params,
        approvedWithdrawalKind(params.amount, params.tokenAddress),
      );
    const pool = requireEnabled();
    const tokenAddress = ethers.getAddress(params.tokenAddress);
    const recipient = ethers.getAddress(params.recipient);
    const fee = params.fee ?? 0n;
    if (fee < 0n || fee > params.amount)
      fail("withdraw fee exceeds the amount");
    // Caught here rather than at the relayer: the fee is bound into the proof,
    // so a zero-fee relayed withdrawal cannot be repriced after proving.
    if (params.relay && fee === 0n) {
      fail("a relayed withdrawal must carry a nonzero relayer fee");
    }
    const { provider, chainId } = await requireProvider(params.signer);
    const all = await authenticateAndDedupeCandidates({
      candidates: candidateNotes(params.notes, pool, chainId, tokenAddress),
      pool,
      chainId,
      tokenAddress,
      spendingKeys: params.spendingKeys,
    });
    const targetDomain = await readCurrentTokenDomain(
      provider,
      pool,
      tokenAddress,
    );
    const currentTreeIndex = targetDomain.treeIndex;
    const current = all.filter(
      ({ note }) => note.treeIndex === currentTreeIndex,
    );
    let selected: StoredJoinSplitNote[];
    try {
      selected = selectForAmount(current, params.amount);
    } catch {
      const retiredGroups = new Map<number, StoredJoinSplitNote[]>();
      for (const note of all) {
        if (note.note.treeIndex === currentTreeIndex) continue;
        const group = retiredGroups.get(note.note.treeIndex!) || [];
        group.push(note);
        retiredGroups.set(note.note.treeIndex!, group);
      }
      const exact = [...retiredGroups.values()]
        .map((group) => selectExact(group, params.amount))
        .find((group): group is StoredJoinSplitNote[] => group !== null);
      if (!exact) {
        fail("retired-tree funds require migration before this withdrawal");
      }
      selected = exact;
    }
    const total = selected.reduce((sum, note) => sum + note.note.value, 0n);
    const outputs: JoinSplitNote[] = [];
    if (total > params.amount) {
      if (selected[0].note.treeIndex !== currentTreeIndex) {
        fail("a retired-tree withdrawal cannot create change; migrate first");
      }
      outputs.push(
        await createFlatJoinSplitV2Note({
          recipientMpk: params.spendingKeys.mpk,
          chainId,
          poolAddress: pool,
          treeIndex: currentTreeIndex,
          tokenAddress,
          tokenHash: selected[0].note.tokenHash,
          value: total - params.amount,
        }),
      );
    }
    if (params.relay && usesPoolRelayQuotes())
      await assertQuotedRelayFee(
        {
          kind: "withdraw",
          token: tokenAddress,
          inputCount: selected.length,
          outputCount: outputs.length as 0 | 1,
          recipients: [recipient],
        },
        fee,
        params.signal,
      );
    params.onLog("Preparing a depth-24 private withdrawal witness", "info");
    const settled = await proveAndSend({
      ...params,
      provider,
      pool,
      chainId,
      tokenAddress,
      recipient,
      withdrawAmount: params.amount,
      fee,
      selected,
      outputs,
      kind: "withdraw",
      deliveries: selfDeliveries(outputs, params.spendingKeys),
      relay: params.relay,
    });
    return {
      success: true,
      txHash: settled.txHash,
      spentNotes: selected.map((note) => note.note),
      outputNotes: settled.outputs,
      changeNote: settled.outputs[0],
      journalId: settled.journalId,
    };
  } catch (error) {
    return {
      success: false,
      spentNotes: [],
      outputNotes: [],
      error: error instanceof Error ? error.message : String(error),
    };
  }
}

export async function executeFlatJoinSplitV2BatchWithdraw(
  params: FlatJoinSplitV2BatchWithdrawParams,
): Promise<FlatJoinSplitV2OperationResult> {
  try {
    params.items.forEach((item) =>
      assertPublicWithdrawalRecipient(item.recipient),
    );
    const pool = requireEnabled();
    const tokenAddress = ethers.getAddress(params.tokenAddress);
    const recipients = params.items.map((item) => item.recipient);
    const amounts = params.items.map((item) => item.amount);
    // Fails fast on count/zero/oversize problems before any chain reads.
    const { total } = validateFlatJoinSplitV2WithdrawBatchEntries(
      recipients,
      amounts,
    );
    const fee = params.fee ?? 0n;
    if (fee < 0n || fee > total) fail("batch fee exceeds the recipient total");
    // Caught here rather than at the relayer: the fee is bound into the proof,
    // so a zero-fee relayed withdrawal cannot be repriced after proving.
    if (params.relay && fee === 0n) {
      fail("a relayed withdrawal must carry a nonzero relayer fee");
    }
    const withdrawAmount = total + fee;
    const { provider, chainId } = await requireProvider(params.signer);
    const all = await authenticateAndDedupeCandidates({
      candidates: candidateNotes(params.notes, pool, chainId, tokenAddress),
      pool,
      chainId,
      tokenAddress,
      spendingKeys: params.spendingKeys,
    });
    const targetDomain = await readCurrentTokenDomain(
      provider,
      pool,
      tokenAddress,
    );
    const currentTreeIndex = targetDomain.treeIndex;
    // Current tree only: unlike the single withdrawal there is no exact-match
    // retired-tree fallback yet — migrate first.
    const current = all.filter(
      ({ note }) => note.treeIndex === currentTreeIndex,
    );
    const selected = selectForAmount(current, withdrawAmount);
    const totalSelected = selected.reduce(
      (sum, note) => sum + note.note.value,
      0n,
    );
    const outputs: JoinSplitNote[] = [];
    if (totalSelected > withdrawAmount) {
      outputs.push(
        await createFlatJoinSplitV2Note({
          recipientMpk: params.spendingKeys.mpk,
          chainId,
          poolAddress: pool,
          treeIndex: currentTreeIndex,
          tokenAddress,
          tokenHash: selected[0].note.tokenHash,
          value: totalSelected - withdrawAmount,
        }),
      );
    }
    if (params.relay && usesPoolRelayQuotes())
      await assertQuotedRelayFee(
        {
          kind: "withdraw_batch",
          token: tokenAddress,
          inputCount: selected.length,
          outputCount: outputs.length as 0 | 1,
          recipients,
        },
        fee,
        params.signal,
      );
    params.onLog(
      `Preparing one witness paying ${recipients.length} recipients`,
      "info",
    );
    const settled = await proveAndSend({
      ...params,
      provider,
      pool,
      chainId,
      tokenAddress,
      recipients,
      amounts,
      withdrawAmount,
      fee,
      selected,
      outputs,
      kind: "withdraw_batch",
      deliveries: selfDeliveries(outputs, params.spendingKeys),
      relay: params.relay,
    });
    return {
      success: true,
      txHash: settled.txHash,
      spentNotes: selected.map((note) => note.note),
      outputNotes: settled.outputs,
      changeNote: settled.outputs[0],
      journalId: settled.journalId,
    };
  } catch (error) {
    return {
      success: false,
      spentNotes: [],
      outputNotes: [],
      error: error instanceof Error ? error.message : String(error),
    };
  }
}
